// src/components/surveys/questions/MultipleChoiceQuestion.tsx
import React from 'react';
import { SurveyQuestion, QuestionOption } from '../../../types/survey.types';
import { Check } from 'lucide-react';

interface MultipleChoiceQuestionProps {
  question: SurveyQuestion;
  value?: string | null;
  onChange?: (optionId: string) => void;
  disabled?: boolean;
  showValidation?: boolean;
  error?: string;
  mode?: 'edit' | 'preview' | 'response';
}

const MultipleChoiceQuestion: React.FC<MultipleChoiceQuestionProps> = ({
  question,
  value,
  onChange,
  disabled = false,
  showValidation = false,
  error,
  mode = 'response',
}) => {
  const options = question.options || [];

  const handleSelect = (optionId: string) => {
    if (!disabled && onChange) {
      onChange(optionId);
    }
  };

  if (mode === 'edit') {
    return (
      <div className="space-y-2">
        <div className="text-sm text-gray-500 mb-2">Options:</div>
        {options.map((option, index) => (
          <div
            key={option.id}
            className="flex items-center p-3 border border-gray-200 rounded-lg bg-gray-50"
          >
            <div className="w-5 h-5 border-2 border-gray-300 rounded-full mr-3" />
            <span className="text-gray-700">{option.text || `Option ${index + 1}`}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          onClick={() => handleSelect(option.id)}
          disabled={disabled}
          className={`w-full flex items-center p-3 border-2 rounded-lg transition-all text-left ${
            value === option.id
              ? 'border-indigo-500 bg-indigo-50'
              : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/50'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <div
            className={`w-5 h-5 border-2 rounded-full mr-3 flex items-center justify-center ${
              value === option.id ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300'
            }`}
          >
            {value === option.id && <div className="w-2 h-2 bg-white rounded-full" />}
          </div>
          <span className={value === option.id ? 'text-indigo-700 font-medium' : 'text-gray-700'}>
            {option.text}
          </span>
        </button>
      ))}
      {showValidation && error && (
        <p className="text-sm text-red-600 mt-2">{error}</p>
      )}
    </div>
  );
};

export default MultipleChoiceQuestion;
